export const FEATURE_FLAGS = {
  ENGAGEMENT_INSIGHTS_UNIFIED_RANGE: "engagement_insights_unified_range",
} as const;

export type FeatureFlagName = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

export type FeatureFlagState = Record<FeatureFlagName, boolean>;

export const FEATURE_FLAG_STORAGE_KEY = "mailer_frontend.featureFlags";
export const FEATURE_FLAG_EVENT_NAME = "feature-flags-updated";

const FEATURE_FLAG_ENV_PREFIX = "REACT_APP_FEATURE_FLAG_";
const FEATURE_FLAG_ENV_MAP_KEY = "REACT_APP_FEATURE_FLAGS";

export const defaultFeatureFlags: FeatureFlagState = {
  [FEATURE_FLAGS.ENGAGEMENT_INSIGHTS_UNIFIED_RANGE]: true,
};

type FeatureFlagDictionary = Partial<Record<FeatureFlagName, boolean>>;

interface FeatureFlagWindow extends Window {
  __FEATURE_FLAGS__?: Record<string, unknown>;
}

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["1", "true", "yes", "on", "enabled"].includes(normalized)) {
      return true;
    }
    if (["0", "false", "no", "off", "disabled"].includes(normalized)) {
      return false;
    }
  }

  if (typeof value === "number") {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
  }

  return undefined;
};

const filterValidFlags = (input: unknown): FeatureFlagDictionary => {
  if (!input || typeof input !== "object") {
    return {};
  }

  const source = input as Record<string, unknown>;
  const result: FeatureFlagDictionary = {};

  Object.values(FEATURE_FLAGS).forEach((flagName) => {
    const parsed = parseBoolean(source[flagName]);
    if (typeof parsed === "boolean") {
      result[flagName] = parsed;
    }
  });

  return result;
};

const toEnvSuffix = (flagName: FeatureFlagName): string =>
  flagName.replace(/[^a-zA-Z0-9]/g, "_").toUpperCase();

const getEnvironmentFlagKey = (flagName: FeatureFlagName): string =>
  `${FEATURE_FLAG_ENV_PREFIX}${toEnvSuffix(flagName)}`;

const getEnvironmentOverrides = (): FeatureFlagDictionary => {
  const env = process.env as Record<string, string | undefined>;
  const overrides: FeatureFlagDictionary = {};

  const asJson = env[FEATURE_FLAG_ENV_MAP_KEY];
  if (asJson) {
    try {
      Object.assign(overrides, filterValidFlags(JSON.parse(asJson)));
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Failed to parse REACT_APP_FEATURE_FLAGS payload", error);
      }
    }
  }

  Object.values(FEATURE_FLAGS).forEach((flagName) => {
    const envValue = env[getEnvironmentFlagKey(flagName)];
    const parsed = parseBoolean(envValue);
    if (typeof parsed === "boolean") {
      overrides[flagName] = parsed;
    }
  });

  return overrides;
};

const getStoredOverrides = (): FeatureFlagDictionary => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const stored = window.localStorage.getItem(FEATURE_FLAG_STORAGE_KEY);
    if (!stored) {
      return {};
    }
    return filterValidFlags(JSON.parse(stored));
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Failed to parse stored feature flags", error);
    }
    return {};
  }
};

const getWindowOverrides = (): FeatureFlagDictionary => {
  if (typeof window === "undefined") {
    return {};
  }

  const globalFlags = (window as FeatureFlagWindow).__FEATURE_FLAGS__;
  if (!globalFlags) {
    return {};
  }

  return filterValidFlags(globalFlags);
};

export const mergeFeatureFlags = (
  ...sources: Array<FeatureFlagDictionary | null | undefined>
): FeatureFlagState => {
  const merged: FeatureFlagState = { ...defaultFeatureFlags };

  sources.forEach((source) => {
    if (!source) {
      return;
    }

    Object.values(FEATURE_FLAGS).forEach((flagName) => {
      const nextValue = source[flagName];
      if (typeof nextValue === "boolean") {
        merged[flagName] = nextValue;
      }
    });
  });

  return merged;
};

export const resolveFeatureFlags = (
  overrides?: FeatureFlagDictionary
): FeatureFlagState =>
  mergeFeatureFlags(
    defaultFeatureFlags,
    getEnvironmentOverrides(),
    getStoredOverrides(),
    getWindowOverrides(),
    overrides
  );

export const readFeatureFlagsFromStorage = (): FeatureFlagDictionary =>
  getStoredOverrides();

export const persistFeatureFlags = (state: FeatureFlagState): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      FEATURE_FLAG_STORAGE_KEY,
      JSON.stringify(state)
    );
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Unable to persist feature flags", error);
    }
  }
};

export const broadcastFeatureFlagUpdate = (
  state: FeatureFlagState
): void => {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return;
  }

  const event = new CustomEvent<FeatureFlagState>(FEATURE_FLAG_EVENT_NAME, {
    detail: state,
  });
  window.dispatchEvent(event);
};
